import { useCallback, useMemo } from 'react';
import { useAuthStore } from '@/shared/model';
import { useMyTeams } from '@/entities/team';
import { toViewEligibilities, useCreatorListEligibility } from '@/entities/creator-list';
import {
  requireManager,
  requireGlobalQuestAccess,
  requireTeamAdmin,
} from '@/shared/lib/useRouteGuard';
import type { TeamCapability, AuthUser, ViewEligibility } from '@aegislab/shared';

export interface PageContext {
  user: AuthUser | null;
  userId: string | undefined;
  isPrivileged: boolean;
  isManager: boolean;
  isTeamAdmin: boolean;
  teamCapabilities: TeamCapability[];
  /** 소속 팀 id 집합 — 퀘스트 액션 소유(팀장 교체 인계) 판정용. useMyTeams(/teams/my-teams) 파생. */
  myTeamIds: string[];
  /** 어느 팀(부서·TF)에서든 팀장인가 — 토큰 teamRole 은 부서만 담아 TF 팀장을 못 싣는다. */
  leadsTeam: boolean;
  isLoading: boolean;
  /** 소속 팀 조회 실패 축 — teamCapabilities/myTeamIds 의 [] 를 "실패"와 "진짜 빈 팀"으로 가른다. */
  isError: boolean;
  /** 팀 조회 재시도 손잡이 — 실패 UI가 붙잡을 안정 참조. */
  refetchTeams: () => void;
  /**
   * 서버가 판정해 준 창 자격(명단 내려받기 — 시딩 팀장). 확인 전·실패는 [] 다.
   * 독·창 가드·미리 불러오기가 `ViewPermissionGrants.eligibilities` 로 같은 값을 넘긴다.
   */
  viewEligibilities: ViewEligibility[];
  /** 자격 조회의 확인 중·실패 축 — 창 가드가 "자격 없음"과 가른다. */
  isEligibilityLoading: boolean;
  isEligibilityError: boolean;
  refetchEligibility: () => void;
}

/**
 * MainPage의 전역 컨텍스트 정보를 제공하는 훅
 *
 * - 사용자 인증 정보
 * - 권한 체크 (관리자, 매니저, 팀 어드민)
 * - 팀 역량 정보 (모든 소속 팀의 역량 통합)
 */
export function usePageContext(): PageContext {
  // Auth & Role
  const user = useAuthStore((s) => s.user);
  const userId = user?.id;
  const isPrivileged = user ? requireGlobalQuestAccess(user) : false;
  const isManager = user ? requireManager(user) : false;
  const isTeamAdmin = user ? requireTeamAdmin(user) : false;

  // Team Capabilities (union of all my teams' capabilities)
  const { data: myTeams, isLoading, isError, refetch } = useMyTeams();
  const refetchTeams = useCallback(() => {
    // 결과 Promise 는 의도적으로 버린다 — 상태는 useMyTeams 가 다시 내보내므로 호출측은 () => void 로 족하다.
    void refetch();
  }, [refetch]);
  const teamCapabilities = useMemo<TeamCapability[]>(() => {
    if (!myTeams) return [];
    const allCaps = myTeams.flatMap((t) => t.capabilities ?? []);
    return [...new Set(allCaps)];
  }, [myTeams]);
  // 소속 팀 id 집합 — 퀘스트 액션 소유(팀장 교체 인계) 판정 SSOT. 백엔드 canManageQuest 의
  // findMyTeams(user) 와 동일 소스(/teams/my-teams). ※ getMyTeams 가 CLUB 팀은 제외하므로
  // DEPARTMENT/TASK_FORCE 에서만 백엔드 집합과 완전 일치(CLUB 소유 TEAM 퀘스트는 under-show, 안전).
  const myTeamIds = useMemo(() => myTeams?.map((t) => t.id) ?? [], [myTeams]);

  const leadsTeam = useMemo(() => (myTeams ?? []).some((t) => t.teamRole === 'LEADER'), [myTeams]);

  const eligibility = useCreatorListEligibility();
  const viewEligibilities = useMemo(
    () => toViewEligibilities(eligibility.data),
    [eligibility.data]
  );
  const refetchEligibilityQuery = eligibility.refetch;
  const refetchEligibility = useCallback(() => {
    void refetchEligibilityQuery();
  }, [refetchEligibilityQuery]);

  return {
    user,
    userId,
    isPrivileged,
    isManager,
    isTeamAdmin,
    teamCapabilities,
    myTeamIds,
    leadsTeam,
    isLoading,
    isError,
    refetchTeams,
    viewEligibilities,
    isEligibilityLoading: eligibility.isLoading,
    isEligibilityError: eligibility.isError,
    refetchEligibility,
  };
}
